import Dialog from "@mui/material/Dialog";
import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "../../components/shared/AlertDialog";
import {
  Box,
  ButtonPrimary,
  HStack,
  isCompanySettingsReadOnly,
  VStack,
} from "../../components/utils";
import AddOrEditSecurityClass from "./AddOrEditSecurityClass";
import { CTADropdown } from "../../components/shared/Dropdown";
import {
  useDeleteSecurityClass,
  useGetSecurityClasses,
} from "../../queries/securityClass";
import { formatDisplayDate } from "../../utils/date";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";

function SecurityClass() {
  const isUserReadOnly = isCompanySettingsReadOnly();
  const gridApi = useRef<GridApi | null>(null);
  const { data: _securityClasses } = useGetSecurityClasses();
  const currencyType = getCurrencyType();

  const [dialog, setDialog] = useState<{
    open: boolean;
    mode?: "Add" | "Edit";
    data?: any;
  }>({
    open: false,
    mode: "Add",
    data: undefined,
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    data?: any;
  }>({
    open: false,
    data: undefined,
  });

  const securityClassesData = _securityClasses;

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  useEffect(() => {
    if (securityClassesData !== undefined) {
      const formatted = securityClassesData.map((item: any) => ({
        ...item,
        issueDate: item.issueDate ? formatDisplayDate(item.issueDate) : "",
        endDate: item.endDate ? formatDisplayDate(item.endDate) : "",
      }));
      setFilteredRowData(formatted);
    }
  }, [securityClassesData]);

  function handleAction(
    row: any,
    action: { name: string; disabled?: boolean }
  ) {
    if (action.name === "Edit Security Class" && !action.disabled) {
      setDialog({ open: true, mode: "Edit", data: row });
    } else if (action.name === "Delete Security Class" && !action.disabled) {
      setDeleteDialog({ open: true, data: row });
    }
  }

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "SECURITY NAME",
        field: "securityName",
        filter: "agTextColumnFilter",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "SECURITY TYPE",
        field: "securityType",
        filter: "agTextColumnFilter",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "SECURITY CLASS",
        field: "securityClass",
        filter: "agTextColumnFilter",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "ISIN",
        field: "isin",
        filter: "agTextColumnFilter",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "FACE VALUE",
        field: "faceValue",
        filter: "agNumberColumnFilter",
        sortable: true,
        autoHeight: true,
        wrapText: true,
        valueFormatter: (params) => formatCurrency(params.value, currencyType),
      },
      {
        headerName: "NOTES",
        field: "notes",
        filter: "agTextColumnFilter",
        sortable: true,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "",
        field: "actions",
        pinned: "right",
        hide: false,
        width: 80,
        maxWidth: 80,
        filter: false,
        colId: "action-column",
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        resizable: false,
        sortable: false,
        menuTabs: ["columnsMenuTab"],
        cellRenderer: CTADropdown,
        cellRendererParams: (params: any) => ({
          actions: [
            { name: "Edit Security Class", disabled: isUserReadOnly },
            { name: "Delete Security Class", disabled: isUserReadOnly },
          ],
          onAction: (action: any) => handleAction(params.data, action),
        }),
      },
    ],
    [isUserReadOnly]
  );

  const [filteredRowData, setFilteredRowData] =
    useState<any>(securityClassesData);

  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  const { mutate: deleteSecurityClass } = useDeleteSecurityClass();
  function deleteSecurityClassEntry() {
    deleteSecurityClass(deleteDialog.data?.id || "", {
      onSuccess: () => {
        toast("Security class deleted successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setDeleteDialog({ open: false });
      },
      onError: (err: any) => {
        toast(err.response.data.reason, { type: "error", autoClose: 2000 });
        setDeleteDialog({ open: false });
      },
    });
    setDeleteDialog({ open: false });
  }

  return (
    <Box className="w-full p-4 bg-white rounded-lg ">
      <HStack className="items-center justify-between mb-8">
        <VStack className="min-w-max">
          <p className="text-lg font-medium text-gray-dark">Security Classes</p>
          <p className="text-xs font-medium text-gray-light">
            {filteredRowData?.length} Security Classes
          </p>
        </VStack>
        <ButtonPrimary
          disabled={isUserReadOnly}
          onClick={() => {
            setDialog({ open: true });
          }}
        >
          Add Security Class
        </ButtonPrimary>
        <Dialog open={dialog.open} maxWidth="sm" fullWidth>
          <AddOrEditSecurityClass
            onClose={() => setDialog({ open: false })}
            mode={dialog.mode || "Add"}
            data={dialog.data}
          />
        </Dialog>
      </HStack>

      <HStack className="justify-between w-full">
        <Box
          style={{
            height: "400px",
          }}
          className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
        >
          <AgGridReact
            rowData={filteredRowData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            defaultColDef={defaultColDef}
            suppressRowTransform={true}
            animateRows={true}
            pagination={true}
            rowClass={
              "border-t border-dashed cursor-pointer hover:bg-slate-50 "
            }
            overlayNoRowsTemplate={
              '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
            }
          />
        </Box>
      </HStack>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false })}
        maxWidth="md"
      >
        <AlertDialog
          onClose={() => setDeleteDialog({ open: false })}
          open={deleteDialog.open}
          message={"Do you want to delete this security class?"}
          onPrimaryAction={deleteSecurityClassEntry}
          onSecondaryAction={() => setDeleteDialog({ open: false })}
        />
      </Dialog>
    </Box>
  );
}

export default SecurityClass;
